import { forwardRef, useState } from 'react';
import { motion } from 'framer-motion';
import { skills } from '../../data/skills';
import './Skills.css';

const Skills = forwardRef((props, ref) => {
  const [activeCategory, setActiveCategory] = useState(0);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const categoryVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const skillItemVariants = {
    hidden: { 
      opacity: 0, 
      x: -30 
    },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const progressVariants = {
    hidden: { width: 0 },
    visible: (level) => ({
      width: `${level}%`,
      transition: {
        duration: 1.2,
        delay: 0.3,
        ease: "easeInOut"
      }
    })
  };

  return (
    <section id="skills" className="skills" ref={ref}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="skills-header"
        >
          <h2 className="section-title">
            <span className="title-accent">My</span> Skills
          </h2>
          <p className="section-subtitle">
            Technologies and tools I work with to bring ideas to life
          </p>
        </motion.div>

        {/* Category Tabs */}
        <motion.div 
          className="category-tabs"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {skills.map((category, index) => (
            <button
              key={category.category}
              className={`category-tab ${activeCategory === index ? 'active' : ''}`}
              onClick={() => setActiveCategory(index)}
            >
              {category.icon && <span className="tab-icon">{category.icon}</span>}
              {category.category}
            </button>
          ))}
        </motion.div>

        {/* Skills Container */}
        <motion.div 
          className="skills-container"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Mobile View - All Categories */}
          <div className="skills-mobile">
            {skills.map((category, index) => (
              <motion.div 
                key={category.category}
                className="skill-category"
                variants={categoryVariants}
              >
                <div className="category-header">
                  {category.icon && <span className="category-icon">{category.icon}</span>}
                  <h3>{category.category}</h3>
                  <span className="category-count">{category.items.length} skills</span>
                </div>
                
                <div className="skill-items">
                  {category.items.map((skill, skillIndex) => (
                    <motion.div 
                      key={skill.name} 
                      className="skill-item"
                      variants={skillItemVariants}
                      whileHover={{ 
                        scale: 1.02,
                        transition: { duration: 0.2 }
                      }}
                    >
                      <div className="skill-info">
                        <div className="skill-name-wrapper">
                          {skill.icon && <span className="skill-icon">{skill.icon}</span>}
                          <span className="skill-name">{skill.name}</span>
                        </div>
                        <span className="skill-level">{skill.level}%</span>
                      </div>
                      
                      <div className="skill-bar">
                        <motion.div 
                          className="skill-progress"
                          custom={skill.level}
                          variants={progressVariants}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          style={{
                            background: `linear-gradient(90deg, 
                              var(--primary-color) 0%, 
                              var(--secondary-color) 100%)`
                          }}
                        />
                        <div className="skill-bar-bg" />
                      </div>
                      
                      {skill.experience && (
                        <div className="skill-experience">
                          {skill.experience}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Desktop View - Tabbed */}
          <div className="skills-desktop">
            <motion.div
              key={activeCategory}
              className="skill-category active"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="category-header">
                {skills[activeCategory].icon && (
                  <span className="category-icon">{skills[activeCategory].icon}</span>
                )}
                <h3>{skills[activeCategory].category}</h3>
                <span className="category-count">
                  {skills[activeCategory].items.length} skills
                </span>
              </div>
              
              <div className="skill-items">
                {skills[activeCategory].items.map((skill, skillIndex) => (
                  <motion.div 
                    key={skill.name} 
                    className="skill-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      duration: 0.4, 
                      delay: skillIndex * 0.1 
                    }}
                    whileHover={{ 
                      scale: 1.02,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <div className="skill-info">
                      <div className="skill-name-wrapper">
                        {skill.icon && <span className="skill-icon">{skill.icon}</span>}
                        <span className="skill-name">{skill.name}</span>
                      </div>
                      <span className="skill-level">{skill.level}%</span>
                    </div>
                    
                    <div className="skill-bar">
                      <motion.div 
                        className="skill-progress"
                        initial={{ width: 0 }}
                        animate={{ width: `${skill.level}%` }}
                        transition={{ 
                          duration: 1,
                          delay: skillIndex * 0.1 + 0.3,
                          ease: "easeInOut"
                        }}
                      />
                      <div className="skill-bar-bg" />
                    </div>
                    
                    {skill.experience && (
                      <div className="skill-experience">
                        {skill.experience}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Skills Summary */}
        <motion.div
          className="skills-summary"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          
        </motion.div>
      </div>
    </section>
  );
});

Skills.displayName = 'Skills';

export default Skills;